import { CurrentLocationState, OrderFulfillmentType } from 'shop/components'
import {
  BaseAddress,
  CartErrorMessage,
  CartProductError,
  CheckoutErrorCode,
  ConsumerCartError,
  ConsumerCartPayload,
  ConsumerCartWarning,
  Fulfillment,
  OrderItem,
  OrderItemWithLimit,
  OrderItemWithVatPrices
} from 'shop/types/cart'
import {
  DELIVERY_FULFILLMENT,
  DeliveryAddress,
  FulfillmentType,
  ORDER_AT_TABLE_FULFILLMENT,
  PICKUP_FULFILLMENT,
  ASAP_ORDER
} from 'shop/types'
import { parseTimezoneISO } from './date'
import { addDays, format, isBefore, isPast, parseISO } from 'date-fns'
import { buildAddressFromComponents } from 'shop/components/Landing/addressUtils'
import { timeString } from 'shop/components/Landing/utils'

/** Create default cart params on shop page */
export const createDefaultCartParams = (
  storeId: string,
  defaultFulfillmentType: FulfillmentType,
  defaultFulfillmentInfo: OrderFulfillmentType,
  savedCurrentLocation: CurrentLocationState | null // session storage of saved-current-location
): ConsumerCartPayload => {
  const fulfillmentWindow = defaultFulfillmentInfo.earliestTimeslot
  const fulfillmentTime =
    defaultFulfillmentInfo.orderType === ASAP_ORDER
      ? null
      : format(parseTimezoneISO(fulfillmentWindow.from), 'HH:mm')

  // load location from session storage
  let savedDeliveryAddress
  let savedAddressId
  if (savedCurrentLocation) {
    // check for a saved address and extract the addressId
    if (savedCurrentLocation.addressId) {
      savedAddressId = savedCurrentLocation.addressId
    } else {
      savedDeliveryAddress = constructDeliveryAddress(
        savedCurrentLocation,
        defaultFulfillmentType.toLowerCase(),
        { consumerApi: true }
      ) as BaseAddress
    }
  }

  return {
    fulfillmentDate: format(
      parseTimezoneISO(fulfillmentWindow.from),
      'yyyy-MM-dd'
    ),
    fulfillmentTime: fulfillmentTime,
    fulfillmentType: defaultFulfillmentType.toUpperCase() as FulfillmentType,
    storeId: storeId,
    ...(savedDeliveryAddress && { deliveryAddress: savedDeliveryAddress }),
    ...(savedAddressId && { addressId: savedAddressId })
  }
}

/** Creates delivery address for consumer api/hasura api create cart mutations */
export const constructDeliveryAddress = (
  currentLocation: CurrentLocationState | undefined,
  fulfillmentType: string,
  options: { consumerApi: boolean } = { consumerApi: false }
): BaseAddress | DeliveryAddress | null => {
  if (!!currentLocation && fulfillmentType === 'delivery') {
    if (options.consumerApi) {
      const { line_1, line_2, city, country, zip } = buildAddressFromComponents(
        currentLocation.addressComponents
      )
      return {
        lineOne: line_1,
        lineTwo: line_2,
        city,
        country,
        zip
      } as BaseAddress
    } else {
      const { flatNumber, line_1, line_2, city, country, zip } =
        buildAddressFromComponents(currentLocation.addressComponents)
      return {
        flatNumber,
        line_1,
        line_2,
        city,
        country,
        zip
      } as DeliveryAddress
    }
  } else return null
}

export const fixFulfillmentTime = (
  fulfillmentTime: string | null,
  options: { consumerApi: boolean } = { consumerApi: false }
) => {
  if (fulfillmentTime === 'immediately' || !fulfillmentTime) {
    return options.consumerApi ? null : 'asap'
  }
  return timeString(fulfillmentTime)
}

export const getFulfillmentFlags = (
  fulfillmentType: FulfillmentType | undefined
): { isOrderAtTable: boolean; isDelivery: boolean; isPickup: boolean } => {
  if (!fulfillmentType)
    return { isOrderAtTable: false, isDelivery: false, isPickup: false }
  return {
    isOrderAtTable: fulfillmentType === ORDER_AT_TABLE_FULFILLMENT,
    isDelivery: fulfillmentType === DELIVERY_FULFILLMENT,
    isPickup: fulfillmentType === PICKUP_FULFILLMENT
  }
}

/** Checks if expected delivery/pickup date is before current date (is expired),
 * if cart order type is asap then cart date is not expired,
 * otherwise compare 'from' in fulfillment window with current datetime */
export const isCartDateExpired = (fulfillment?: Fulfillment) =>
  !!fulfillment &&
  fulfillment.orderType !== ASAP_ORDER &&
  isBefore(new Date(fulfillment.window.from), new Date())

export const getCurrentCartId = (domain: string) => {
  const cartDetails = localStorage.getItem(domain)

  if (!cartDetails) {
    return null
  }

  try {
    const parsedDetails = JSON.parse(cartDetails)

    const expiresAt = parsedDetails['expires_at']
    if (expiresAt && isPast(parseISO(expiresAt))) {
      localStorage.removeItem(domain)
      return null
    }

    return parsedDetails.cart_id || null
  } catch {
    localStorage.removeItem(domain)
    return null
  }
}

export const getAlcoholicItems = (orderItems: OrderItem[]) =>
  orderItems.filter((item: OrderItem) => item.restrictions.alcoholic)

export const isCartProductError = (
  validation: CheckoutErrorCode
): validation is CartProductErrorCode => {
  return [
    CartErrorMessage.INVALID_MODIFIERS,
    CartErrorMessage.PRODUCTS_OUT_OF_STOCK,
    CartErrorMessage.PRODUCTS_UNAVAILABLE,
    CartErrorMessage.PRODUCT_PRICES_CHANGED,
    CartErrorMessage.PRODUCT_LIMIT_REACHED
  ].includes(validation as CartProductErrorCode)
}

export type CartProductErrorCode =
  | CartErrorMessage.PRODUCTS_OUT_OF_STOCK
  | CartErrorMessage.PRODUCTS_UNAVAILABLE
  | CartErrorMessage.PRODUCT_PRICES_CHANGED
  | CartErrorMessage.INVALID_MODIFIERS
  | CartErrorMessage.PRODUCT_LIMIT_REACHED

/** Filters all product errors from the consumer cart errors array */
export const getCurrentProductErrors = (
  consumerCartErrors: ConsumerCartError[]
) => {
  return consumerCartErrors.filter((error) =>
    findErrorWarning([error], isCartProductError)
  ) as CartProductError[]
}

/** Takes warnings/errors array and finds error object using guard function */
export const findErrorWarning = (
  errorsOrWarnings: (ConsumerCartWarning | ConsumerCartError)[],
  errorCodeGuardFunc: (validation: CheckoutErrorCode) => boolean
): ConsumerCartWarning | ConsumerCartError | undefined => {
  return errorsOrWarnings.find((item) => {
    return errorCodeGuardFunc(item.message)
  })
}

export const saveCartToLocalStorage = (
  id: string,
  merchant: { slug: string; id?: string }
) =>
  localStorage.setItem(
    merchant.slug,
    JSON.stringify({
      cart_id: id,
      merchant_id: merchant.id,
      expires_at: addDays(new Date(), 1)
    })
  )

/** Returns OrderItem/OrderItemWithVatPrices of invalid items when provided a cart & item ids */
export const getInvalidItems = (
  productError: CartProductError,
  orderItems: OrderItem[]
): (OrderItem | OrderItemWithVatPrices | OrderItemWithLimit)[] => {
  const filterItemsByVariantId = (errorItems: { productVariantId: string }[]) =>
    orderItems.filter((item) =>
      errorItems.some(
        (errorItem) => item.variantId === errorItem.productVariantId
      )
    )

  const filterItemsById = (errorItems: { id: string }[]) =>
    orderItems.filter((item) =>
      errorItems.some((errorItem) => item.variantId === errorItem.id)
    )

  switch (productError.message) {
    case CartErrorMessage.INVALID_MODIFIERS:
      return filterItemsByVariantId(productError.breakdown || [])

    case CartErrorMessage.PRODUCTS_OUT_OF_STOCK:
    case CartErrorMessage.PRODUCTS_UNAVAILABLE:
      return filterItemsById(productError.variants || [])

    case CartErrorMessage.PRODUCT_PRICES_CHANGED:
      return orderItems.reduce(
        (acc, item) => {
          const matchedProductErrorInfo = productError?.priceComparison.find(
            (errorItem) => item.id === errorItem.orderItemId
          )
          return matchedProductErrorInfo
            ? [
                ...acc,
                {
                  ...item,
                  newPriceWithVat: matchedProductErrorInfo.newPriceWithVat,
                  oldPriceWithVat: matchedProductErrorInfo.oldPriceWithVat
                }
              ]
            : acc
        },
        [] as OrderItem[] | OrderItemWithVatPrices[]
      )

    case CartErrorMessage.PRODUCT_LIMIT_REACHED:
      return orderItems.reduce((acc, item) => {
        const matchedProductErrorInfo = productError?.variants.find(
          (errorItem) => item.variantId === errorItem.id
        )

        return matchedProductErrorInfo
          ? [
              ...acc,
              {
                ...item,
                limit: matchedProductErrorInfo.limit
              }
            ]
          : acc
      }, [] as OrderItemWithLimit[])

    default:
      return []
  }
}

// Legacy type & func, but needed for now
export type Metadata = {
  pickup_type?: string
  recipient?: string
}

export const isTableOrder = (metadata?: Metadata) => {
  if (!metadata) return false

  return metadata?.pickup_type === 'table'
}
